// The market pages' URLs (src/market.ts), in a module of their own so the web bundle can link to them too.
import { BAY_AREA, SCORECARD_STATE } from "./regions.ts";
// re-exported for older importers; the region facts live in shared/regions.ts
export { BAY_AREA, SCORECARD_STATE };

export const slugify = (s: string) => s.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/** How a page names the area: "Mill Valley, CA 94941", "Austin-Round Rock-San Marcos, TX metro", "Texas". */
export function areaTitle(a: { level: string; code: string; name: string }): string {
  if (a.level === "us") return "United States";
  // a ZIP without a place name is named "ZIP 78712" already
  if (a.level === "zip") return a.name.includes(a.code) ? a.name : `${a.name} ${a.code}`;
  if (a.level === "metro") return `${a.name} metro`;
  return a.name;
}

/** Search results cut a title off at about 60 characters. */
export const TITLE_MAX = 60;

/**
 * A market page's <title>: "{Name} Home Prices & Housing Market (Aug 2026)", shortened step by step where a long name
 * (a metro's) would run past TITLE_MAX: "… Home Prices (Aug 2026)", then "… Home Prices". The name always comes first,
 * so whatever a search result cuts off is the end. An area with fire-zone data (California) leads with what the
 * national sites don't have: "{Name} Home Prices & Fire Risk (Aug 2026)", where it fits.
 */
export function marketTitle(name: string, month: string, o: { fire?: boolean } = {}): string {
  const m = month ? ` (${month})` : "";
  const forms = [...(o.fire ? [`${name} Home Prices & Fire Risk${m}`] : []), `${name} Home Prices & Housing Market${m}`, `${name} Home Prices${m}`];
  return forms.find((t) => t.length <= TITLE_MAX) ?? `${name} Home Prices`;
}

/** The slug an area's name gives: "salt-lake-county-ut", "omaha-ne-68154" (a ZIP's carries its code). */
export const marketSlug = (a: { level: string; code: string; name: string }) => slugify(a.level === "zip" ? areaTitle(a) : a.name);

/**
 * The canonical path of an area's market page: /market/<level>/<code>/<slug>. The slug is for people only (any slug
 * redirects to this one), and once the area is published it's fixed: `slug` is the one on record (geo.slug,
 * ingest/urls.ts), which a later rename doesn't change. Without it, the name's.
 */
export function marketPath(a: { level: string; code: string; name: string; slug?: string | null }): string {
  if (a.level === "us") return "/market/us";
  return `/market/${a.level}/${a.code}/${a.slug || marketSlug(a)}`;
}

/** Whether an area has a scorecard page: a California county or city (one with a forecast; the page 404s otherwise). */
export const hasScorecard = (a: { level: string; geo_id: string }) =>
  (a.level === "county" || a.level === "city") && a.geo_id.slice(a.level.length + 1).startsWith(SCORECARD_STATE);

/** The path of an area's forecast scorecard: /scorecard/<level>/<code>/<slug>. */
export const scorecardPath = (a: { level: string; code: string; name: string; slug?: string | null }) => marketPath(a).replace(/^\/market\//, "/scorecard/");
